import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface Category {
  id: string;
  label: string;
  icon: string;
  image: string;
  active: boolean;
  description: string;
}

function mapRow(row: Record<string, unknown>): Category {
  return {
    id: (row.id as string) ?? '',
    label: (row.label as string) ?? '',
    icon: (row.icon as string) ?? 'ri-heart-pulse-line',
    image: (row.image as string) ?? '',
    active: Boolean(row.active),
    description: (row.description as string) ?? '',
  };
}

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('categories')
        .select('*')
        .order('created_at', { ascending: true });

      if (err) throw err;
      setCategories((data ?? []).map(mapRow));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar categorias');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const addCategory = async (cat: Omit<Category, 'id'>): Promise<Category | null> => {
    try {
      const { data, error: err } = await supabase
        .from('categories')
        .insert({
          label: cat.label,
          icon: cat.icon,
          image: cat.image,
          active: cat.active,
          description: cat.description,
        })
        .select()
        .single();

      if (err) throw err;
      const newCat = mapRow(data);
      setCategories(prev => [...prev, newCat]);
      return newCat;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao criar categoria');
      return null;
    }
  };

  const updateCategory = async (id: string, updates: Partial<Category>): Promise<boolean> => {
    try {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.label !== undefined) dbUpdates.label = updates.label;
      if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
      if (updates.image !== undefined) dbUpdates.image = updates.image;
      if (updates.active !== undefined) dbUpdates.active = updates.active;
      if (updates.description !== undefined) dbUpdates.description = updates.description;

      const { error: err } = await supabase
        .from('categories')
        .update(dbUpdates)
        .eq('id', id);

      if (err) throw err;
      setCategories(prev => prev.map(c => (c.id === id ? { ...c, ...updates } : c)));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao atualizar categoria');
      return false;
    }
  };

  const deleteCategory = async (id: string): Promise<boolean> => {
    try {
      const { error: err } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (err) throw err;
      setCategories(prev => prev.filter(c => c.id !== id));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao excluir categoria');
      return false;
    }
  };

  const toggleActive = async (id: string) => {
    const cat = categories.find(c => c.id === id);
    if (!cat) return;
    await updateCategory(id, { active: !cat.active });
  };

  const getActiveCategories = () => categories.filter(c => c.active);

  const getCategoryById = (id: string) => categories.find(c => c.id === id);

  const getCategoryLabel = (id: string) =>
    categories.find(c => c.id === id)?.label ?? id;

  return {
    categories,
    loading,
    error,
    fetchCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleActive,
    getActiveCategories,
    getCategoryById,
    getCategoryLabel,
  };
}